import type { PortInfoListItem } from '../types'

export interface VisitInfo {
  visitId: string
  dateOfArrival: string
}

export interface EntryInfo {
  entryId: string
  subject?: string
  vesselName?: string
  imoNumber?: string
  dateOfInformation?: string
}

export interface BerthInfo {
  berthName: string
  visits: VisitInfo[]
}

export interface TerminalGroup {
  terminalName: string
  berths: BerthInfo[]
}

export interface PortGroup {
  portName: string
  portCode: string
  portCountry: string
  terminals: TerminalGroup[]
}

export interface ContinentGroup {
  continentName: string
  continentCode: string
  entries: EntryInfo[]
}

export interface CountryGroup {
  countryName: string
  countryCode: string
  continentName?: string
  entries: EntryInfo[]
}

export interface GroupedPortInfo {
  continents: ContinentGroup[]
  countries: CountryGroup[]
  ports: PortGroup[]
}

export function groupPortInfoByCategory(
  items: PortInfoListItem[],
): GroupedPortInfo {
  const continentMap = new Map<string, ContinentGroup>()
  const countryMap = new Map<string, CountryGroup>()
  const portMap = new Map<string, PortGroup>()

  const sortedItems = [...items].sort((a, b) => {
    const dateA = a.dateOfArrival
      ? new Date(a.dateOfArrival).getTime()
      : a.dateOfInformation
        ? new Date(a.dateOfInformation).getTime()
        : 0
    const dateB = b.dateOfArrival
      ? new Date(b.dateOfArrival).getTime()
      : b.dateOfInformation
        ? new Date(b.dateOfInformation).getTime()
        : 0
    return dateB - dateA
  })

  for (const item of sortedItems) {
    const itemType = item.type || 'port'

    if (itemType === 'continent' && item.continentCode) {
      let continentGroup = continentMap.get(item.continentCode)
      if (!continentGroup) {
        continentGroup = {
          continentName: item.continentName || '',
          continentCode: item.continentCode,
          entries: [],
        }
        continentMap.set(item.continentCode, continentGroup)
      }
      continentGroup.entries.push({
        entryId: item._id,
        subject: item.subject,
        vesselName: item.vesselName,
        imoNumber: item.imoNumber,
        dateOfInformation: item.dateOfInformation,
      })
    } else if (itemType === 'country' && item.countryCode) {
      let countryGroup = countryMap.get(item.countryCode)
      if (!countryGroup) {
        countryGroup = {
          countryName: item.countryName || '',
          countryCode: item.countryCode,
          continentName: item.continentName,
          entries: [],
        }
        countryMap.set(item.countryCode, countryGroup)
      }
      countryGroup.entries.push({
        entryId: item._id,
        subject: item.subject,
        vesselName: item.vesselName,
        imoNumber: item.imoNumber,
        dateOfInformation: item.dateOfInformation,
      })
    } else if (item.portName) {
      const portKey = item.portCode || item.portName

      let port = portMap.get(portKey)
      if (!port) {
        port = {
          portName: item.portName,
          portCode: item.portCode || '',
          portCountry: item.portCountry || '',
          terminals: [],
        }
        portMap.set(portKey, port)
      }

      if (item.terminalName) {
        let terminal = port.terminals.find(
          (t) => t.terminalName === item.terminalName,
        )
        if (!terminal) {
          terminal = { terminalName: item.terminalName, berths: [] }
          port.terminals.push(terminal)
        }

        if (item.berthName) {
          let berth = terminal.berths.find(
            (b) => b.berthName === item.berthName,
          )
          if (!berth) {
            berth = { berthName: item.berthName, visits: [] }
            terminal.berths.push(berth)
          }

          berth.visits.push({
            visitId: item._id,
            dateOfArrival: item.dateOfArrival || '',
          })
        }
      }
    }
  }

  return {
    continents: Array.from(continentMap.values())
      .sort((a, b) => a.continentName.localeCompare(b.continentName))
      .map((group) => ({
        ...group,
        entries: group.entries.sort((a, b) => {
          const dateA = a.dateOfInformation
            ? new Date(a.dateOfInformation).getTime()
            : 0
          const dateB = b.dateOfInformation
            ? new Date(b.dateOfInformation).getTime()
            : 0
          return dateB - dateA
        }),
      })),
    countries: Array.from(countryMap.values())
      .sort((a, b) => a.countryName.localeCompare(b.countryName))
      .map((group) => ({
        ...group,
        entries: group.entries.sort((a, b) => {
          const dateA = a.dateOfInformation
            ? new Date(a.dateOfInformation).getTime()
            : 0
          const dateB = b.dateOfInformation
            ? new Date(b.dateOfInformation).getTime()
            : 0
          return dateB - dateA
        }),
      })),
    ports: Array.from(portMap.values()).sort((a, b) =>
      a.portName.localeCompare(b.portName),
    ),
  }
}
